/**
 * QaSidebar — Contextual QA testing panel
 *
 * Shows test items relevant to the current route.
 * Traffic light buttons with aggregated tester counts.
 * Bug report form for ad-hoc issues.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { FlaskConical, Bug, ChevronDown, MessageSquare, Camera, Send, X, ExternalLink, ArrowRight } from 'lucide-react';
import { endpoints, qaHeaders } from './config';
import QaTestDetailModal from './QaTestDetailModal';

// ─── Tester ID (persistent per device) ────────────────────────

function getTesterId() {
  let id = localStorage.getItem('kuutio-qa-tester-id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('kuutio-qa-tester-id', id);
  }
  return id;
}

function getTesterName() {
  return localStorage.getItem('kuutio-qa-tester-name') || '';
}

function setTesterName(name) {
  localStorage.setItem('kuutio-qa-tester-name', name);
}

function currentWeek() {
  const d = new Date();
  const onejan = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

// ─── Animations ─────────────────────────────────────────

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

// ─── Styled Components ──────────────────────────────────

const TabButton = styled.button`
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 900;
  writing-mode: vertical-rl;
  padding: 12px 8px;
  background: rgba(20, 20, 30, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-right: none;
  border-radius: 8px 0 0 8px;
  color: #aaa;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(30, 30, 45, 0.95);
    color: #fff;
  }

  svg { width: 14px; height: 14px; }
`;

const DotIndicator = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
  flex-shrink: 0;
`;

const Panel = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 360px;
  background: rgba(15, 15, 25, 0.98);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 950;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.2s ease-out;
  backdrop-filter: blur(12px);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;

  svg { width: 18px; height: 18px; color: #c084fc; }
`;

const WeekBadge = styled.span`
  font-size: 11px;
  color: #888;
  background: rgba(255, 255, 255, 0.05);
  padding: 3px 8px;
  border-radius: 4px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  &:hover { color: #fff; background: rgba(255, 255, 255, 0.1); }
`;

const TesterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  input {
    flex: 1;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 6px 10px;
    color: #fff;
    font-size: 12px;
    outline: none;
    &:focus { border-color: rgba(147, 51, 234, 0.5); }
    &::placeholder { color: #555; }
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  select {
    flex: 1;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 6px 10px;
    color: #fff;
    font-size: 12px;
    outline: none;
    cursor: pointer;
    option { background: #1a1a2e; }
  }
`;

const ProgressBar = styled.div`
  padding: 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 11px;
  color: #888;
`;

const ProgressTrack = styled.div`
  height: 4px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  margin-top: 6px;
  overflow: hidden;
  display: flex;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.$color};
  width: ${props => props.$pct}%;
  transition: width 0.3s;
`;

const ItemList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const Item = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  transition: background 0.15s;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const ItemTitle = styled.div`
  font-size: 13px;
  color: #ddd;
  margin-bottom: 6px;
  line-height: 1.4;
`;

const ItemControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const TrafficBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.15s;
  min-width: 36px;
  justify-content: center;

  background: ${props => props.$active
    ? props.$variant === 'green' ? 'rgba(34, 197, 94, 0.2)'
    : props.$variant === 'yellow' ? 'rgba(250, 204, 21, 0.2)'
    : 'rgba(239, 68, 68, 0.2)'
    : 'rgba(255, 255, 255, 0.03)'};
  border-color: ${props => props.$active
    ? props.$variant === 'green' ? 'rgba(34, 197, 94, 0.4)'
    : props.$variant === 'yellow' ? 'rgba(250, 204, 21, 0.4)'
    : 'rgba(239, 68, 68, 0.4)'
    : 'rgba(255, 255, 255, 0.08)'};
  color: ${props => props.$active
    ? props.$variant === 'green' ? '#4ade80'
    : props.$variant === 'yellow' ? '#facc15'
    : '#f87171'
    : '#555'};

  &:hover {
    background: ${props =>
      props.$variant === 'green' ? 'rgba(34, 197, 94, 0.15)'
    : props.$variant === 'yellow' ? 'rgba(250, 204, 21, 0.15)'
    : 'rgba(239, 68, 68, 0.15)'};
    color: ${props =>
      props.$variant === 'green' ? '#4ade80'
    : props.$variant === 'yellow' ? '#facc15'
    : '#f87171'};
  }
`;

const CommentToggle = styled.button`
  background: none;
  border: none;
  color: ${props => props.$hasComment ? '#c084fc' : '#555'};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  &:hover { color: #c084fc; background: rgba(147, 51, 234, 0.1); }
  svg { width: 14px; height: 14px; }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px 4px;
  margin-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  &:first-of-type { margin-top: 0; border-top: none; }
`;

const GroupLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const GroupNavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: #666;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  &:hover { color: #c084fc; background: rgba(147, 51, 234, 0.1); }
  svg { width: 12px; height: 12px; }
`;

const ROUTE_LABELS = {
  '/admin': 'Admin Hub',
  '/admin/knowledge': 'Aineistot',
  '/admin/prompts': 'Promptit',
  '/admin/activity-log': 'Loki',
  '/admin/qa': 'QA Dashboard',
  '/impactpath': 'Vaikutuspolut',
  '/ratkaisut': 'Ratkaisut',
  '/lenses': 'Linssit',
  '/pivotpoints': 'Vipupisteet',
};

const CommentBox = styled.div`
  margin-top: 6px;
  textarea {
    width: 100%;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 8px;
    color: #ccc;
    font-size: 12px;
    resize: vertical;
    min-height: 48px;
    outline: none;
    font-family: inherit;
    &:focus { border-color: rgba(147, 51, 234, 0.4); }
  }
`;

const BugSection = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 12px 16px;
`;

const BugHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #f87171;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  &:hover { color: #fca5a5; }
  svg { width: 14px; height: 14px; }
`;

const BugForm = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  input, textarea {
    width: 100%;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(239, 68, 68, 0.2);
    border-radius: 6px;
    padding: 8px;
    color: #ccc;
    font-size: 12px;
    outline: none;
    font-family: inherit;
    &:focus { border-color: rgba(239, 68, 68, 0.4); }
    &::placeholder { color: #555; }
  }

  textarea { min-height: 48px; resize: vertical; }
`;

const BugSubmit = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: #f87171;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: rgba(239, 68, 68, 0.25); }
  svg { width: 14px; height: 14px; }
`;

// ─── Component ──────────────────────────────────────────

export default function QaSidebar({ embedded = false, items: staticItems = [] }) {
  // Consumer passes their own test items (QA_TEST_ITEMS for Kuutio, custom for others)
  const QA_TEST_ITEMS = staticItems;
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [results, setResults] = useState({});  // { itemId: [{ tester_id, status, comment }] }
  const [expandedComment, setExpandedComment] = useState(null);
  const [comments, setComments] = useState({});  // { itemId: 'text' }
  const [showBugForm, setShowBugForm] = useState(false);
  const [bugTitle, setBugTitle] = useState('');
  const [bugDesc, setBugDesc] = useState('');
  const [filter, setFilter] = useState('route');  // 'route' | 'all' | 'untested'
  const [testerName, setTesterNameState] = useState(getTesterName());
  const [loading, setLoading] = useState(false);
  const [detailModalItem, setDetailModalItem] = useState(null);

  const week = currentWeek();
  const testerId = getTesterId();
  const route = location.pathname;

  // Determine which admin routes show the sidebar
  const isAdminRoute = route.startsWith('/admin') || route === '/ratkaisut'
    || route === '/lenses' || route === '/impactpath' || route === '/pivotpoints';

  // Fetch results from backend
  const fetchResults = useCallback(async () => {
    try {
      const res = await fetch(`${endpoints.results(week)}?route=${filter === 'route' ? route : ''}`, { headers: qaHeaders() });
      if (!res.ok) return;
      const data = await res.json();

      if (data.items && data.items.length > 0) {
        setItems(data.items);
        const resultMap = {};
        for (const item of data.items) {
          resultMap[item.id] = item.results || [];
        }
        setResults(resultMap);
      } else {
        // Backend has no items (not seeded yet) — use static definitions
        const filtered = filter === 'route'
          ? QA_TEST_ITEMS.filter(i => i.routes.includes(route))
          : QA_TEST_ITEMS;
        setItems(filtered.map(i => ({ ...i, results: [], counts: { green: 0, yellow: 0, red: 0, total: 0 } })));
      }
    } catch {
      // Network error — use static items
      const filtered = filter === 'route'
        ? QA_TEST_ITEMS.filter(i => i.routes.includes(route))
        : QA_TEST_ITEMS;
      setItems(filtered.map(i => ({ ...i, results: [], counts: { green: 0, yellow: 0, red: 0, total: 0 } })));
    }
  }, [week, route, filter]);

  useEffect(() => {
    if (isOpen || embedded) fetchResults();
  }, [isOpen, embedded, fetchResults]);

  // Filter items based on current selection
  const visibleItems = filter === 'untested'
    ? items.filter(i => !i.counts || i.counts.total === 0)
    : filter === 'route'
    ? items.filter(i => i.routes?.includes(route))
    : items;

  // My current status per item
  const myStatus = (itemId) => {
    const itemResults = results[itemId] || [];
    const mine = itemResults.find(r => r.tester_id === testerId);
    return mine?.status || null;
  };

  // Save result
  const saveResult = useCallback(async (itemId, status) => {
    const comment = comments[itemId] || '';
    try {
      await fetch(endpoints.resultsSave(), {
        method: 'PUT',
        headers: qaHeaders(),
        body: JSON.stringify({
          itemId, status, comment,
          week, testerId,
          testerName: testerName || undefined,
          route,
          browserInfo: { userAgent: navigator.userAgent, viewport: `${window.innerWidth}x${window.innerHeight}` },
        }),
      });
      fetchResults();
    } catch (err) {
      console.error('QA save error:', err);
    }
  }, [week, testerId, testerName, route, comments, fetchResults]);

  // Save comment on blur
  const saveComment = useCallback(async (itemId) => {
    const current = myStatus(itemId);
    if (current && comments[itemId]) {
      saveResult(itemId, current);
    }
  }, [myStatus, comments, saveResult]);

  // Submit bug
  const submitBug = useCallback(async () => {
    if (!bugTitle.trim()) return;
    try {
      await fetch(endpoints.bugs(), {
        method: 'POST',
        headers: qaHeaders(),
        body: JSON.stringify({
          title: bugTitle,
          description: bugDesc || undefined,
          route,
          testerId,
          testerName: testerName || undefined,
          browserInfo: { userAgent: navigator.userAgent, viewport: `${window.innerWidth}x${window.innerHeight}` },
        }),
      });
      setBugTitle('');
      setBugDesc('');
      setShowBugForm(false);
    } catch (err) {
      console.error('Bug report error:', err);
    }
  }, [bugTitle, bugDesc, route, testerId, testerName]);

  // Update tester name
  const handleNameChange = (e) => {
    setTesterNameState(e.target.value);
    setTesterName(e.target.value);
  };

  // Summary for dot indicator
  const totalItems = QA_TEST_ITEMS.filter(i => i.routes.includes(route)).length;
  const testedCount = Object.values(results).filter(r => r.some(x => x.tester_id === testerId)).length;
  const hasRed = Object.values(results).some(r => r.some(x => x.status === 'red'));
  const hasYellow = Object.values(results).some(r => r.some(x => x.status === 'yellow'));
  const dotColor = hasRed ? '#ef4444' : hasYellow ? '#facc15' : totalItems > testedCount ? '#c084fc' : '#22c55e';

  const qaEnabled = localStorage.getItem('kuutio-qa-enabled') === 'true';
  if (!embedded && (!isAdminRoute || !qaEnabled)) return null;
  // In embedded mode (drawer tab), always render content — drawer handles visibility

  // Progress stats
  const totalVisible = visibleItems.length;
  const greenCount = visibleItems.filter(i => i.counts?.green > 0 && !i.counts?.red && !i.counts?.yellow).length;
  const yellowCount = visibleItems.filter(i => i.counts?.yellow > 0 && !i.counts?.red).length;
  const redCount = visibleItems.filter(i => i.counts?.red > 0).length;
  const untestedCount = totalVisible - greenCount - yellowCount - redCount;

  // ── Onboarding: if no tester name saved, show welcome first ──
  const needsOnboarding = !getTesterName();
  const [onboardingName, setOnboardingName] = useState('');

  const onboardingView = (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>
        Tervetuloa testaamaan!
      </div>
      <div style={{ fontSize: 13, color: '#999', lineHeight: 1.5 }}>
        Uusia ominaisuuksia on julkaistu. Testaa ne suoraan täältä — klikkaa liikennevaloja arvioidaksesi toimivatko ne oikein.
      </div>
      <div style={{ fontSize: 13, color: '#888', lineHeight: 1.5 }}>
        Kirjoita nimesi aloittaaksesi:
      </div>
      <input
        type="text"
        placeholder="Nimesi"
        value={onboardingName}
        onChange={(e) => setOnboardingName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onboardingName.trim()) {
            setTesterName(onboardingName.trim());
            setTesterNameState(onboardingName.trim());
          }
        }}
        style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(147,51,234,0.4)',
          borderRadius: 6,
          padding: '10px 14px',
          color: '#fff',
          fontSize: 14,
          outline: 'none',
          fontFamily: 'inherit',
        }}
        autoFocus
      />
      <button
        onClick={() => {
          if (!onboardingName.trim()) return;
          setTesterName(onboardingName.trim());
          setTesterNameState(onboardingName.trim());
        }}
        disabled={!onboardingName.trim()}
        style={{
          padding: '10px 16px',
          background: onboardingName.trim() ? 'rgba(147,51,234,0.2)' : 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(147,51,234,0.4)',
          borderRadius: 6,
          color: onboardingName.trim() ? '#c084fc' : '#555',
          fontSize: 14,
          fontWeight: 600,
          cursor: onboardingName.trim() ? 'pointer' : 'default',
          fontFamily: 'inherit',
        }}
      >
        Aloita testaus
      </button>
    </div>
  );

  // ── Embedded mode: render content directly for DrawerSystem ──
  if (embedded) {
    if (needsOnboarding) return onboardingView;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <TesterRow>
            <input
              type="text"
              placeholder="Nimesi (vapaaehtoinen)"
              value={testerName}
              onChange={handleNameChange}
            />
          </TesterRow>

          <FilterRow>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="route">Tämä näkymä ({QA_TEST_ITEMS.filter(i => i.routes.includes(route)).length})</option>
              <option value="all">Kaikki ({QA_TEST_ITEMS.length})</option>
              <option value="untested">Testaamatta</option>
            </select>
          </FilterRow>

          {totalVisible > 0 && (
            <ProgressBar>
              Testattu: {totalVisible - untestedCount}/{totalVisible}
              <ProgressTrack>
                <ProgressFill $color="#22c55e" $pct={(greenCount / totalVisible) * 100} />
                <ProgressFill $color="#facc15" $pct={(yellowCount / totalVisible) * 100} />
                <ProgressFill $color="#ef4444" $pct={(redCount / totalVisible) * 100} />
              </ProgressTrack>
            </ProgressBar>
          )}

          <ItemList>
            {(() => {
              // Group by route when showing all/untested, flat list for current view
              const showGroups = filter !== 'route';
              const groups = {};
              if (showGroups) {
                for (const item of visibleItems) {
                  const groupRoute = item.routes?.[0] || 'other';
                  if (!groups[groupRoute]) groups[groupRoute] = [];
                  groups[groupRoute].push(item);
                }
              } else {
                groups['_current'] = visibleItems;
              }

              return Object.entries(groups).map(([groupRoute, groupItems]) => (
                <div key={groupRoute}>
                  {showGroups && (
                    <GroupHeader>
                      <GroupLabel>{ROUTE_LABELS[groupRoute] || groupRoute}</GroupLabel>
                      {groupRoute !== route && (
                        <GroupNavBtn onClick={() => navigate(groupRoute)}>
                          Siirry <ArrowRight />
                        </GroupNavBtn>
                      )}
                    </GroupHeader>
                  )}
                  {groupItems.map(item => {
                    const my = myStatus(item.id);
                    const counts = item.counts || { green: 0, yellow: 0, red: 0, total: 0 };
                    const hasComment = !!comments[item.id];

                    return (
                      <Item key={item.id} onClick={() => {
                        const staticItem = QA_TEST_ITEMS.find(q => q.id === item.id);
                        setDetailModalItem({ ...item, ...staticItem });
                      }}>
                        <ItemTitle>
                          {item.title}
                        </ItemTitle>
                        <ItemControls onClick={e => e.stopPropagation()}>
                          <TrafficBtn
                            $variant="green"
                            $active={my === 'green'}
                            onClick={() => saveResult(item.id, 'green')}
                            title="Toimii"
                          >
                            🟢{counts.green > 0 && ` ${counts.green}`}
                          </TrafficBtn>
                          <TrafficBtn
                            $variant="yellow"
                            $active={my === 'yellow'}
                            onClick={() => saveResult(item.id, 'yellow')}
                            title="Osittain"
                          >
                            🟡{counts.yellow > 0 && ` ${counts.yellow}`}
                          </TrafficBtn>
                          <TrafficBtn
                            $variant="red"
                            $active={my === 'red'}
                            onClick={() => saveResult(item.id, 'red')}
                            title="Ei toimi"
                          >
                            🔴{counts.red > 0 && ` ${counts.red}`}
                          </TrafficBtn>
                          <CommentToggle
                            $hasComment={hasComment || expandedComment === item.id}
                            onClick={() => setExpandedComment(expandedComment === item.id ? null : item.id)}
                          >
                            <MessageSquare />
                          </CommentToggle>
                        </ItemControls>
                        {expandedComment === item.id && (
                          <CommentBox>
                            <textarea
                              placeholder="Kommentti..."
                              value={comments[item.id] || ''}
                              onChange={(e) => setComments(prev => ({ ...prev, [item.id]: e.target.value }))}
                              onBlur={() => saveComment(item.id)}
                            />
                          </CommentBox>
                        )}
                      </Item>
                    );
                  })}
                </div>
              ));
            })()}

            {visibleItems.length === 0 && (
              <Item style={{ textAlign: 'center', color: '#555' }}>
                {filter === 'untested' ? 'Kaikki testattu! 🎉' : 'Ei testattavaa tällä sivulla'}
              </Item>
            )}
          </ItemList>

          <BugSection>
            <BugHeader onClick={() => setShowBugForm(!showBugForm)}>
              <Bug />
              Raportoi bugi
              <ChevronDown size={12} style={{ transform: showBugForm ? 'rotate(180deg)' : 'none', transition: '0.15s' }} />
            </BugHeader>
            {showBugForm && (
              <BugForm>
                <input
                  type="text"
                  placeholder="Bugin otsikko"
                  value={bugTitle}
                  onChange={(e) => setBugTitle(e.target.value)}
                />
                <textarea
                  placeholder="Kuvaus (vapaaehtoinen)"
                  value={bugDesc}
                  onChange={(e) => setBugDesc(e.target.value)}
                />
                <BugSubmit onClick={submitBug} disabled={!bugTitle.trim()}>
                  <Send /> Lähetä
                </BugSubmit>
              </BugForm>
            )}
          </BugSection>

        {detailModalItem && (
          <QaTestDetailModal
            item={detailModalItem}
            currentStatus={myStatus(detailModalItem.id)}
            currentComment={comments[detailModalItem.id] || ''}
            onSave={async (itemId, status, comment) => {
              try {
                await fetch(endpoints.resultsSave(), {
                  method: 'PUT',
                  headers: qaHeaders(),
                  body: JSON.stringify({
                    itemId, status, comment: comment || undefined,
                    week, testerId,
                    testerName: testerName || undefined,
                    route,
                    browserInfo: { userAgent: navigator.userAgent, viewport: `${window.innerWidth}x${window.innerHeight}` },
                  }),
                });
                if (comment) setComments(prev => ({ ...prev, [itemId]: comment }));
                fetchResults();
              } catch (err) {
                console.error('QA modal save error:', err);
              }
              setDetailModalItem(null);
            }}
            onClose={() => setDetailModalItem(null)}
          />
        )}
      </div>
    );
  }

  // ── Standalone floating mode ──
  return (
    <>
      {!isOpen && totalItems > 0 && (
        <TabButton onClick={() => setIsOpen(true)} data-qa-sidebar>
          <FlaskConical />
          <DotIndicator $color={dotColor} />
          Testaa
        </TabButton>
      )}

      {isOpen && (
        <Panel data-qa-sidebar>
          <PanelHeader>
            <PanelTitle>
              <FlaskConical />
              Testaa
            </PanelTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <WeekBadge>{week}</WeekBadge>
              <CloseBtn onClick={() => setIsOpen(false)}>
                <X size={16} />
              </CloseBtn>
            </div>
          </PanelHeader>

          <QaSidebar embedded />
        </Panel>
      )}
    </>
  );
}
